import { Check } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import { Spinner } from '@myhealth-raccoon/components';
import { useAsyncData } from '@myhealth-raccoon/services';

import { AvailabilityTimeslotPayload } from '.';
import { TimeslotButton } from './TimeslotButton';
import {
    AppointmentType,
    AutomedClinicAvailabilityTimeModel,
    AutomedDoctorAvailabilityTimeModel,
    SiteBookingAvailabilityModel,
    UserBookingDetails,
    availabilityService,
} from 'src/services/availability';
import { BookingUserProfileModel } from 'src/services/booking';
import { money, subtractSurchargeFromFee } from 'src/utils/renderMoney';

type Props = {
    appointmentTypes: AppointmentType[];
    preselectedAppointmentType?: AppointmentType;
    siteId: string | number;
    className?: string;
    onSelectTimeSlot(appointment: AvailabilityTimeslotPayload): void;
    onSelectAppointmentType?(appointmentType: AppointmentType): void;
    selected?: Moment;
    disabled?: boolean;
    allUsers?: BookingUserProfileModel[];
};

type AppointmentTypeTimesProps = {
    appointmentType: AppointmentType;
    siteId: string | number;
    onSelectTimeSlot(appointment: AvailabilityTimeslotPayload): void;
    selected?: Moment;
    disabled?: boolean;
    allUsers?: BookingUserProfileModel[];
};

function AppointmentTypeTimes({
    appointmentType,
    siteId,
    onSelectTimeSlot,
    selected,
    disabled,
    allUsers,
}: AppointmentTypeTimesProps) {
    const dates = useMemo(
        () => ({
            startDate: moment().toDate(),
            endDate: moment().add(12, 'hours').toDate(),
        }),
        []
    );

    const { data, ...request } = useAsyncData(
        {
            method: availabilityService.GetBookingTimeslot,
            service: availabilityService,
            selector: (data: SiteBookingAvailabilityModel[]) => {
                return data
                    .reduce(
                        (curr, doc) => {
                            for (const [date, timeSlots] of Object.entries(doc.timeSlots)) {
                                for (const ts of timeSlots) {
                                    const dateTime = moment(`${date} ${ts.t}`);
                                    const existingTimeslot = curr.find((x) => x.dateTime.isSame(dateTime, 'minute'));

                                    if (existingTimeslot) {
                                        existingTimeslot.users.push(doc.user);
                                    } else {
                                        curr.push({
                                            ts,
                                            dateTime,
                                            duration: ts.d,
                                            users: [doc.user],
                                        });
                                    }
                                }
                            }
                            return curr;
                        },
                        [] as {
                            dateTime: Moment;
                            duration: string;
                            users: UserBookingDetails[];
                            ts: AutomedClinicAvailabilityTimeModel;
                        }[]
                    )
                    .sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf())
                    .filter(({ dateTime }) => dateTime.diff(undefined, 'hours') < 12);
            },
        },
        siteId,
        appointmentType.appointmentTypeId,
        null,
        dates
    );

    return request.isLoading ? (
        <div className="flex h-44 items-center justify-center">
            <Spinner size="md" />
        </div>
    ) : data?.length ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {data.map(({ dateTime, duration, users, ts }) => (
                <TimeslotButton
                    key={dateTime.valueOf()}
                    defaultDuration={Number(duration)}
                    selected={selected}
                    disabled={disabled}
                    time={dateTime}
                    onClick={(t) =>
                        onSelectTimeSlot({
                            date: dateTime.toDate(),
                            doctorId: t.userId,
                            appointmentTypeId: appointmentType.appointmentTypeId,
                            clinicId: Number(siteId),
                            timeSlot: t.ts as AutomedDoctorAvailabilityTimeModel,
                        })
                    }
                    allUsers={allUsers}
                    ts={{
                        time: ts.t,
                        slots: users.map((u) => ({ userId: u.userId, duration: ts.d, ts })),
                    }}
                />
            ))}
        </div>
    ) : (
        <div className="text-error flex h-44 items-center justify-center text-center font-medium">
            {request.error ? request.error.message : 'There are no times available for this appointment type.'}
        </div>
    );
}

export function AfterHoursAvailability({
    appointmentTypes,
    preselectedAppointmentType,
    siteId,
    className,
    onSelectTimeSlot,
    onSelectAppointmentType,
    selected: selectedTime,
    disabled,
    allUsers,
}: Props) {
    const [selected, setSelected] = useState(preselectedAppointmentType || appointmentTypes[0]);
    // when the prop changes, change the selected
    useEffect(() => {
        if (preselectedAppointmentType) {
            setSelected(preselectedAppointmentType);
        }
    }, [preselectedAppointmentType]);

    useEffect(() => {
        onSelectAppointmentType?.(selected);
    }, [selected, onSelectAppointmentType]);

    const { max, min } = useMemo(
        () =>
            appointmentTypes.reduce(
                ({ min, max }, at) => ({
                    min: Math.min(
                        min,
                        subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees))
                    ),
                    max: Math.max(
                        max,
                        subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees))
                    ),
                }),
                { max: 0, min: Infinity }
            ),
        [appointmentTypes]
    );

    return (
        <>
            <div className={classNames(className, 'space-y-6')}>
                {appointmentTypes.map((at) => {
                    const fee = subtractSurchargeFromFee(Number(at.prePaymentFees) + Number(at.postBillingFees));
                    const scaledFee = (fee - min) / (max - min);
                    return (
                        <div
                            key={at.appointmentTypeId}
                            className={classNames('rounded-xl bg-white', {
                                'border border-gray-200': at !== selected,
                                'border-primary-600 ring-primary-600 border ring-1': at === selected,
                            })}
                        >
                            <button
                                className={classNames('flex w-full flex-row items-center gap-2.5 p-4 text-left', {})}
                                onClick={at === selected ? undefined : () => setSelected(at)}
                                disabled={at === selected}
                            >
                                <h3 className="flex-1 text-base font-bold text-gray-700">{at.appointmentName}</h3>
                                <p
                                    className={classNames('rounded-lg px-2.5 py-1 font-medium', {
                                        'bg-success-50 text-success-700': scaledFee < 0.33,
                                        'bg-warning-50 text-warning-700': scaledFee >= 0.33 && scaledFee < 0.66,
                                        'bg-error-50 text-error-700': scaledFee >= 0.66,
                                    })}
                                >
                                    ${money(fee)}
                                </p>
                                {at === selected ? (
                                    <i className="bg-primary relative flex h-4 w-4 items-center justify-center rounded-full">
                                        <Check className="intset-0 absolute m-auto block h-3 w-3 text-white" />
                                    </i>
                                ) : (
                                    <i className="block h-4 w-4 rounded-full border border-gray-300"></i>
                                )}
                            </button>
                            {at === selected && (
                                <>
                                    <hr className="border-primary-600 border-t-2" />
                                    <div className="p-4">
                                        <AppointmentTypeTimes
                                            disabled={disabled}
                                            appointmentType={at}
                                            siteId={siteId}
                                            onSelectTimeSlot={onSelectTimeSlot}
                                            selected={selectedTime}
                                            allUsers={allUsers}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
